<template>
  <vs-popup :active.sync="isActive" title="Cari Hesap Ödeme Girişi" class="cari-payment-popup">
    <div class="p-6">
      <!-- Cari Seçimi -->
      <div class="mb-5">
        <label class="vs-input--label text-sm">Cari Hesap Seçiniz</label>
        <vs-select
          class="w-full mt-1"
          v-model="selectedCariId"
          autocomplete
          placeholder="Cari hesap arayın veya seçin..."
          @change="onCariChange"
        >
          <vs-select-item
            v-for="(item, index) in cariList"
            :key="index"
            :value="item.id"
            :text="item.name"
          />
        </vs-select>
      </div>

      <!-- Açık Bakiye Gösterimi -->
      <div v-if="selectedCari" class="mb-5 p-4 rounded-lg bg-light text-center border-dashed border-2">
        <h5 class="text-secondary font-semibold">Güncel Açık Bakiye</h5>
        <div class="text-2xl font-bold mt-2" :class="balanceClass">
          {{ formatPrice(balanceInfo.remainingBalance) }} TL
        </div>
        <div class="flex justify-around mt-3 text-xs text-secondary">
          <span>Toplam Borç: {{ formatPrice(balanceInfo.totalDebt) }} TL</span>
          <span>Toplam Ödenen: {{ formatPrice(balanceInfo.totalPaid) }} TL</span>
        </div>
      </div>

      <!-- Ödeme Tipi Seçimi -->
      <div class="mb-5" v-if="selectedCari">
        <label class="vs-input--label text-sm">Ödeme Tipi</label>
        <vs-select
          class="w-full mt-1"
          v-model="paymentType"
          placeholder="Ödeme tipi seçin..."
          @change="onPaymentTypeChange"
        >
          <vs-select-item value="nakit" text="Nakit" />
          <vs-select-item value="kredi" text="Kredi Kartı" />
          <vs-select-item value="yemek" text="Yemek Kartı" />
          <vs-select-item value="iban" text="IBAN" />
        </vs-select>
      </div>

      <!-- Yemek Kartı Detayı -->
      <div class="mb-5" v-if="selectedCari && paymentType === 'yemek'">
        <label class="vs-input--label text-sm">Yemek Kartı Seçiniz</label>
        <vs-select
          class="w-full mt-1"
          v-model="selectedMealCardId"
          placeholder="Yemek kartı seçin..."
        >
          <vs-select-item
            v-for="(card, index) in mealCards"
            :key="index"
            :value="card.id"
            :text="card.name"
          />
        </vs-select>
      </div>

      <!-- Tutar Girişi -->
      <div class="mb-5" v-if="selectedCari">
        <vs-input
          class="w-full"
          label="Ödeme Tutarı"
          v-model="amount"
          v-validate="'required|decimal:2'"
          name="Amount"
          placeholder="0.00"
          @input="handleAmountInput"
        />
        <span class="text-danger text-sm" v-show="errors.has('Amount')">Geçerli bir tutar giriniz.</span>
      </div>

      <!-- Açıklama Girişi -->
      <div class="mb-5" v-if="selectedCari">
        <vs-input
          class="w-full"
          label="Açıklama"
          v-model="description"
          placeholder="Ödeme ile ilgili not yazabilirsiniz..."
        />
      </div>

      <div class="flex justify-end mt-6" v-if="selectedCari">
        <vs-button @click="isActive = false" type="filled" color="danger" class="mr-3 text-white">Vazgeç</vs-button>
        <vs-button @click="confirmSave" color="success" :disabled="isSaving || !amount || amount <= 0">Ödemeyi Kaydet</vs-button>
      </div>
    </div>

    <!-- Onay Dialogu -->
    <div class="popup-overlay" style="z-index: 99999999;" v-if="showConfirmFlag">
      <div class="popup p-6 text-center">
        <h3 class="text-lg font-bold mb-4">Ödemeyi Onaylıyor musunuz?</h3>
        <p class="mb-6">
          <strong>{{ selectedCari.name }}</strong> cari hesabından 
          <strong>{{ amount }} TL</strong> tutarında 
          <strong>{{ paymentTypeName }}</strong> ödemesi tahsil edilecektir.
        </p>
        <div class="flex justify-center">
          <vs-button @click="showConfirmFlag = false" type="filled" color="danger" class="mr-3 text-white">Vazgeç</vs-button>
          <vs-button @click="submitPayment" color="success">Onayla ve Kaydet</vs-button>
        </div>
      </div>
    </div>
  </vs-popup>
</template>

<script>
import { getAllCari, getCariBalance, saveCariPayment } from "@/api/Cari";
import { getPaymentMethodList } from "@/api/PaymentMethod";

export default {
  data() {
    return {
      isActive: false,
      isSaving: false,
      cariList: [],
      paymentMethods: [],
      mealCards: [],
      selectedCariId: null,
      selectedCari: null,
      balanceInfo: {
        totalDebt: 0,
        totalPaid: 0,
        remainingBalance: 0
      },
      paymentType: '',
      selectedMealCardId: null,
      amount: '',
      description: '',
      showConfirmFlag: false
    };
  },

  computed: {
    activePersonel() {
      return this.$store.getters["selfservice/getActivePersonal"] || {};
    },
    balanceClass() {
      if (this.balanceInfo.remainingBalance > 0) return 'text-danger';
      if (this.balanceInfo.remainingBalance < 0) return 'text-success';
      return 'text-primary';
    },
    paymentTypeName() {
      if (this.paymentType === 'nakit') return 'Nakit';
      if (this.paymentType === 'kredi') return 'Kredi Kartı';
      if (this.paymentType === 'iban') return 'IBAN';
      if (this.paymentType === 'yemek') {
        const card = this.mealCards.find(c => c.id === this.selectedMealCardId);
        return 'Yemek Kartı' + (card ? ` (${card.name})` : '');
      }
      return '';
    }
  },

  watch: {
    isActive(val) {
      if (val) {
        this.resetForm();
        this.fetchCaris();
        this.fetchPaymentMethods();
      }
    }
  },

  methods: {
    resetForm() {
      this.selectedCariId = null;
      this.selectedCari = null;
      this.balanceInfo = { totalDebt: 0, totalPaid: 0, remainingBalance: 0 };
      this.paymentType = '';
      this.selectedMealCardId = null;
      this.amount = '';
      this.description = '';
      this.showConfirmFlag = false;
    },

    async fetchCaris() {
      try {
        const res = await getAllCari();
        this.cariList = res.data || [];
      } catch (err) {
        console.error(err);
      }
    },

    async fetchPaymentMethods() {
      try {
        const res = await getPaymentMethodList();
        this.paymentMethods = res || [];
        // Filter meal cards: under OriginPaymentMethodId = 3 or name matches known Turkish meal cards
        const mealCardNames = ['multinet', 'sodexo', 'pluxee', 'ticket', 'setcard', 'metropol', 'tokenflex', 'paye'];
        this.mealCards = this.paymentMethods.filter(m => {
          const isUnderOthers = m.originPaymentMethodId == this.$othersPaymentId;
          const nameLower = (m.name || '').toLowerCase();
          const matchesName = mealCardNames.some(name => nameLower.includes(name));
          return (isUnderOthers || matchesName) && m.id !== 10;
        });
      } catch (err) {
        console.error(err);
      }
    },

    async onCariChange() {
      if (!this.selectedCariId) {
        this.selectedCari = null;
        return;
      }
      this.selectedCari = this.cariList.find(c => c.id === this.selectedCariId);
      if (this.selectedCari) {
        try {
          const res = await getCariBalance(this.selectedCariId);
          this.balanceInfo = res.data;
        } catch (err) {
          this.$vs.notify({
            color: 'danger',
            title: 'Hata',
            text: 'Cari bakiye bilgisi alınamadı.'
          });
        }
      }
    },

    onPaymentTypeChange() {
      this.selectedMealCardId = null;
    },

    handleAmountInput() {
      if (this.amount) {
        const regex = /^\d+(\.\d{0,2})?$/;
        if (!regex.test(this.amount)) {
          this.amount = this.amount.slice(0, -1);
        }
      }
    },

    formatPrice(value) {
      if (value === undefined || value === null) return '0,00';
      return new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value);
    },

    async confirmSave() {
      const isValid = await this.$validator.validateAll();
      if (!isValid) return;
      if (!this.paymentType) {
        this.$vs.notify({
          color: 'warning',
          title: 'Uyarı',
          text: 'Lütfen ödeme tipini seçiniz.'
        });
        return;
      }
      if (this.paymentType === 'yemek' && !this.selectedMealCardId) {
        this.$vs.notify({
          color: 'warning',
          title: 'Uyarı',
          text: 'Lütfen yemek kartını seçiniz.'
        });
        return;
      }
      this.showConfirmFlag = true;
    },

    async submitPayment() {
      this.showConfirmFlag = false;
      this.isSaving = true;

      let methodId = 1;
      let mealCardId = null;

      if (this.paymentType === 'nakit') {
        methodId = 1;
      } else if (this.paymentType === 'kredi') {
        methodId = 2;
      } else if (this.paymentType === 'iban') {
        const ibanMethod = this.paymentMethods.find(m => m.name.toUpperCase() === 'IBAN');
        methodId = ibanMethod ? ibanMethod.id : 3;
      } else if (this.paymentType === 'yemek') {
        methodId = 3;
        mealCardId = this.selectedMealCardId;
      }

      const payload = {
        cariId: this.selectedCariId,
        amount: parseFloat(this.amount),
        paymentMethodId: methodId,
        mealCardId: mealCardId,
        description: this.description
      };

      try {
        const res = await saveCariPayment(payload);
        if (res.status === 200) {
          this.$vs.notify({
            color: 'success',
            title: 'Başarılı',
            text: 'Cari hesap ödeme girişi başarıyla kaydedildi.'
          });
          this.isActive = false;
          this.$emit('payment-saved');
        } else {
          this.$vs.notify({
            color: 'danger',
            title: 'Hata',
            text: 'Ödeme kaydedilemedi.'
          });
        }
      } catch (err) {
        this.$vs.notify({
          color: 'danger',
          title: 'Hata',
          text: 'Sunucu bağlantı hatası oluştu.'
        });
      } finally {
        this.isSaving = false;
      }
    }
  }
};
</script>

<style scoped>
.bg-light {
  background-color: rgba(0, 0, 0, 0.03);
}
.border-dashed {
  border-style: dashed;
}
.popup-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
}
.popup {
  background: white;
  border-radius: 12px;
  max-width: 400px;
  width: 90%;
  box-shadow: 0 4px 20px rgba(0,0,0,0.15);
}
</style>
